import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
} from "@chakra-ui/react";
import { navigate } from "@reach/router";
import { TabHeader } from "components/TabHeader";
import { CloseButton } from "components/RFQ/CloseButton";
import SelectTemplate from "components/RFQ/SelectTemplate/SelectTemplate";
import { SupplierListSuffix } from "pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Suppliers/SupplierListSuffix";
import { getSupplierListType } from "newui/features/Rfx/SupplierList/supplier-list-types";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { useRfxProjectTypes } from "newui/features/Rfx/hooks/useRfxProjectTypes";
import { getProjectsWithSupplierLists } from "newui/features/Rfx/Projects";
import { createRfx } from "newui/features/Rfx/api/rfx";
import { RfxProjectTypeSelect } from "newui/components/ui/CommonSelects";
import { SelectWithLabel, SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { Button } from "newui/components/Button";
import { TextInputWithLabel } from "newui/components/ui/InputWithLabel/TextInputWithLabel";
import type { RfxProjectLite, RfxProjectSupplierListLite } from "../types/rfx.types";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";

interface SelectedTemplate {
	label: string;
	value: number | string;
}

interface SelectedRFQOption {
	label: string;
	value: string;
}

interface CreateRFxSliderProps {
	isOpen: boolean;
	projectId?: number | null;
	projectName?: string;
	projectTypeId?: number | null;
	onClose: () => void;
}

export const CreateRFxSlider: React.FC<CreateRFxSliderProps> = ({
	isOpen,
	projectId = null,
	projectName = "",
	projectTypeId: incomingProjectTypeId = null,
	onClose,
}) => {
	const { clientPaths } = useNavigationPaths();
	const { data: projectTypes = [], isLoading: isLoadingTypes } = useRfxProjectTypes();

	const activeTypes = useMemo(
		() => projectTypes.filter((t) => t.isActive),
		[projectTypes]
	);
	const hasMultipleTypes = activeTypes.length > 1;

	// State
	const [selectedProjectTypeId, setSelectedProjectTypeId] = useState<number | null>(
		incomingProjectTypeId
	);
	const [projects, setProjects] = useState<RfxProjectLite[]>([]);
	const [isLoadingProjects, setIsLoadingProjects] = useState(false);
	const [selectedProject, setSelectedProject] = useState<number | null>(projectId);
	const [selectedSupplier, setSelectedSupplier] = useState<SelectOption | null>(null);
	const [suppliers, setSuppliers] = useState<SelectOption[]>([]);
	const [selectedRFQOptions, setSelectedRFQOptions] = useState<SelectedRFQOption | null>(null);
	const [selectedTemplate, setSelectedTemplate] = useState<SelectedTemplate | null>(null);
	const [isCreating, setIsCreating] = useState(false);

	// Auto-select if company has only one project type
	useEffect(() => {
		if (!isLoadingTypes && activeTypes.length === 1 && !selectedProjectTypeId) {
			setSelectedProjectTypeId(activeTypes[0].id);
		}
	}, [isLoadingTypes, activeTypes, selectedProjectTypeId]);

	// Sync incoming props when drawer opens
	useEffect(() => {
		if (isOpen) {
			if (incomingProjectTypeId) {
				setSelectedProjectTypeId(incomingProjectTypeId);
			}
			if (projectId) {
				setSelectedProject(projectId);
			}
		}
	}, [isOpen, incomingProjectTypeId, projectId]);

	// Fetch projects when project type changes
	useEffect(() => {
		if (!selectedProjectTypeId) {
			setProjects([]);
			return;
		}

		setIsLoadingProjects(true);
		getProjectsWithSupplierLists(selectedProjectTypeId)
			.then((res) => {
				setProjects(res ?? []);
			})
			.finally(() => {
				setIsLoadingProjects(false);
			});
	}, [selectedProjectTypeId]);

	const projectOptions: SelectOption[] = useMemo(
		() =>
			projects.map((project) => ({
				value: project.id,
				label: project.name,
			})),
		[projects]
	);

	const buildSupplierOptions = useCallback(
		(projectIdVal: number): SelectOption[] => {
			const project = projects.find((p) => p.id === projectIdVal);
			const suppliersOpt =
				project?.supplier_list?.map((list: RfxProjectSupplierListLite) => ({
					value: list.id,
					label: list.name,
					isCompanyApproved: list.is_company_approved_list === 1,
					suffix: <SupplierListSuffix listType={getSupplierListType(list as any)} />,
				})) ?? [];

			return [
				{ value: "skip_supplier_list", label: "Skip Supplier List" },
				...suppliersOpt,
			];
		},
		[projects]
	);

	// Update supplier options when project changes or projects load
	useEffect(() => {
		if (selectedProject && projects.length > 0) {
			setSuppliers(buildSupplierOptions(selectedProject));
		}
	}, [selectedProject, projects, buildSupplierOptions]);

	const handleProjectTypeChange = (option: SelectOption | null) => {
		setSelectedProjectTypeId(option?.value ?? null);
		setSelectedProject(null);
		setSelectedSupplier(null);
		setSuppliers([]);
		setSelectedRFQOptions(null);
		setSelectedTemplate(null);
	};

	const handleChangeProject = (option: SelectOption | null) => {
		const projectIdVal = option?.value ?? null;
		setSelectedProject(projectIdVal);
		setSelectedSupplier(null);
		setSelectedRFQOptions(null);
		setSelectedTemplate(null);

		if (projectIdVal) {
			setSuppliers(buildSupplierOptions(projectIdVal));
		} else {
			setSuppliers([]);
		}
	};

	const handleChangeSupplier = (option: SelectOption | null) => {
		setSelectedSupplier(option);
		setSelectedRFQOptions(null);
		setSelectedTemplate(null);
	};

	const handleCreateRFx = async () => {
		if (!selectedProject || !selectedSupplier || !selectedRFQOptions) {
			return;
		}

		setIsCreating(true);

		const data = {
			project_id: selectedProject,
			template_id: selectedTemplate?.value || "",
			supplier_list_id:
				selectedSupplier.value === "skip_supplier_list"
					? ""
					: selectedSupplier.value || "",
		};

		try {
			const res = await createRfx(data);
			if (res) {
				navigate(clientPaths?.createRfx(res.id), {
					state: { rfqData: res },
				});
			}
		} finally {
			setIsCreating(false);
		}
	};

	const isProjectDisabled = !selectedProjectTypeId;
	const canCreate =
		!!selectedSupplier && !!selectedProject && !!selectedRFQOptions;

	return (
		<Drawer
			placement="right"
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
		>
			<DrawerOverlay onClick={onClose} />
			<DrawerContent>
				<DrawerHeader borderBottomWidth="1px" className="py-6 px-0">
					<div className="px-6">
						<TabHeader
							noDivider
							heading="SELECT A PROJECT & SUPPLIER"
							description="Select your preferred project and supplier to begin creating your request for a quote."
						>
							<CloseButton onClose={onClose} />
						</TabHeader>
					</div>
				</DrawerHeader>
				<DrawerBody className="max-h-[calc(100vh-100px)] p-6">
					{isLoadingTypes ? <LoadingSpinner />
						:
						<>
							{/* Project Portfolio (Type) */}
							{hasMultipleTypes && (incomingProjectTypeId ? (
								<div className="mb-8">
									<TextInputWithLabel
										label="Project Portfolio"
										readOnly
										value={activeTypes.find((t) => t.id === incomingProjectTypeId)?.name ?? ""}
										disabled
									/>
								</div>
							) : ((
								<div className="mb-8">
									<RfxProjectTypeSelect
										label="Project Portfolio"
										placeholder="Select project portfolio"
										value={selectedProjectTypeId}
										onChange={handleProjectTypeChange}
										required
										testId="select-project-type"
									/>
								</div>)
							))}

							{/* Project */}
							<div className="mb-8">
								{projectId ? (
									<TextInputWithLabel
										label="Project"
										readOnly
										value={projectName}
										disabled
									/>
								) : (
									<SelectWithLabel
										label="Select Project"
										placeholder="Select Project"
										options={projectOptions}
										value={selectedProject}
										onChange={handleChangeProject}
										disabled={isProjectDisabled}
										isLoading={isLoadingProjects}
										testId="select-project-resource"
										required
									/>
								)}
							</div>

							{/* Supplier List */}
							<div
								className="mb-8"
								style={{ opacity: selectedProject ? 1 : 0.5 }}
							>
								<SelectWithLabel
									label="Select Supplier List"
									placeholder="Select Supplier List"
									options={suppliers}
									value={selectedSupplier}
									onChange={handleChangeSupplier}
									disabled={!selectedProject}
									clearAble
									testId="select-supplier-list-resource"
									required
								/>
							</div>

							{/* Template selection */}
							{selectedProject && selectedSupplier && (
								<SelectTemplate
									setSelectedTemplate={setSelectedTemplate}
									selectedRFQOptions={selectedRFQOptions}
									setSelectedRFQOptions={setSelectedRFQOptions}
								/>
							)}

							{/* Create button */}
							<div className="mb-8">
								<Button
									width="100%"
									onClick={handleCreateRFx}
									disabled={!canCreate || isCreating}
									data-testid="create-rfq-drawer"
								>
									{isCreating ? "Creating..." : "Create RFx"}
								</Button>
							</div>
							<div className="text-center">
								<p>
									Haven&apos;t created the project yet?
									<a href={clientPaths?.rfxProjects?.new}>
										{" "}
										Start here
									</a>
								</p>
							</div>
						</>}
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};
